/**
 * FO-059 — pure order-groepering per leverancier voor de uitrusting-bestelmail.
 *
 * Groepeert bestelregels op leverancier (Client type=LEVERANCIER) zodat er per
 * leverancier één bestelmail uitgaat (bv. PBM naar Pehavo, kleding naar Van
 * Ysselt). Regels van een artikel zónder leverancier komen apart terug als
 * `ongekoppeld`: die mogen NIET zomaar verstuurd worden - er is bewust GEEN
 * fallback-leverancier (elk artikel hoort een leverancier te hebben).
 *
 * Bewust no-DB en generiek over de regel-payload `T`, zodat de API-service 'm
 * voedt met Prisma-regels en de unit-test met plain objects.
 */

import { dedupeEmailsCI } from '@streep/types'

export interface LeverancierRegelInput<T> {
  leverancierId: string | null
  leverancierNaam: string | null
  leverancierEmail: string | null
  /**
   * Optionele extra bestelmail-ontvanger(s) van DIT artikel (bv. een op-maat-
   * artikel dat intern moet meelezen). Worden case-insensitief gededupeerd aan
   * de leveranciersgroep toegevoegd; de caller voegt ze toe aan de `to`.
   */
  extraEmails?: string[]
  regel: T
}

export interface LeverancierGroep<T> {
  leverancierId: string
  leverancierNaam: string
  /** null = leverancier heeft geen e-mailadres → caller waarschuwt, verzendt niet. */
  leverancierEmail: string | null
  /** Per-artikel extra ontvangers, case-insensitief gededupeerd (eerste casing wint). */
  extraEmails: string[]
  regels: T[]
}

export interface BestelGroepering<T> {
  /** Eén entry per leverancier, in volgorde van eerste voorkomen. */
  groepen: LeverancierGroep<T>[]
  /** Regels van artikelen zonder leverancier (mogen NIET verzonden worden). */
  ongekoppeld: T[]
}

/**
 * Groepeer bestelregels per `leverancierId`. Behoudt de invoervolgorde (eerste
 * voorkomen bepaalt de groepvolgorde). Naam/e-mail van de eerste regel per
 * leverancier zijn leidend. Regels zonder `leverancierId` → `ongekoppeld`.
 */
export function groepeerPerLeverancier<T>(
  items: LeverancierRegelInput<T>[],
): BestelGroepering<T> {
  const groepMap = new Map<string, LeverancierGroep<T>>()
  const ongekoppeld: T[] = []

  for (const item of items) {
    if (!item.leverancierId) {
      ongekoppeld.push(item.regel)
      continue
    }
    let groep = groepMap.get(item.leverancierId)
    if (!groep) {
      groep = {
        leverancierId: item.leverancierId,
        leverancierNaam: item.leverancierNaam ?? 'Onbekende leverancier',
        leverancierEmail: item.leverancierEmail,
        extraEmails: [],
        regels: [],
      }
      groepMap.set(item.leverancierId, groep)
    }
    groep.regels.push(item.regel)
    // Per-artikel extra ontvangers ruw verzamelen; dedup gebeurt per groep aan het
    // eind via de gedeelde dedupeEmailsCI (één bron, eerste casing wint).
    if (item.extraEmails) groep.extraEmails.push(...item.extraEmails)
  }

  const groepen = Array.from(groepMap.values())
  for (const groep of groepen) {
    groep.extraEmails = dedupeEmailsCI(groep.extraEmails)
  }
  return { groepen, ongekoppeld }
}
